import { ApiError } from '../api/client';
import { store } from '../store/store';
import { showError } from '../store/slices/globalErrorSlice';
import { HTTP_STATUS } from '@/consts';
import { getDialogMessageIfKnown, logError } from '@/utils/errorLogger';

/**
 * Install global error handlers for both synchronous and asynchronous errors.
 *
 * Handles:
 * - window.onerror: synchronous errors thrown in code
 * - window.onunhandledrejection: unhandled Promise rejections
 * - Logs all errors to console in dev environments (local npm run dev and AWS dev)
 *
 * Special handling:
 * - ApiError 401/500: skipped (apiFetch already handles these with navigation)
 * - All other errors: logged to console and shown in global error dialog
 * Should be called once in main.tsx after store is created.
 */
export function installGlobalErrorHandler(): void {
  // Handle synchronous errors thrown anywhere in the app
  window.onerror = (
    message: string | Event,
    _source?: string,
    _lineno?: number,
    _colno?: number,
    error?: Error,
  ) => {
    const messageStr = typeof message === 'string' ? message : 'An error occurred';
    const actualError = error || new Error(messageStr);
    logError(actualError);

    const dialogMessage = getDialogMessageIfKnown(actualError);
    if (dialogMessage !== null) {
      store.dispatch(showError({ message: dialogMessage, statusCode: null }));
    }

    return true; // always suppress — we've logged it and shown a dialog if applicable
  };

  // Handle unhandled Promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason as unknown;

    // Log the error to console
    logError(reason);

    // Handle ApiError instances
    if (reason instanceof ApiError) {
      // Don't show dialog for 401/500 — apiFetch already handles these
      // (401: redirects to login, 500: navigates to /error page)
      if (
        reason.statusCode === HTTP_STATUS.UNAUTHORIZED ||
        reason.statusCode === HTTP_STATUS.INTERNAL_SERVER_ERROR
      ) {
        event.preventDefault();
        return;
      }

      // Show global error dialog for other ApiErrors (400, 404, 409, etc.)
      store.dispatch(
        showError({
          message: reason.message,
          statusCode: reason.statusCode,
        }),
      );

      event.preventDefault();
      return;
    }

    // Show dialog only for known error types; unknown errors are logged but not surfaced to the user
    const dialogMessage = getDialogMessageIfKnown(reason);
    if (dialogMessage !== null) {
      store.dispatch(showError({ message: dialogMessage, statusCode: null }));
    }

    event.preventDefault();
  });
}
